import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import CourseCatalogCard from '../../../components/user/CourseCatalogCard';
import NoData from '../../../components/common/NoData';
import { getTraineeClassroomCourses } from '../../../services/user/classroom.services';
import { encodeData } from '../../../utils/encodeAndEncode';
import useCourseCardProgress from '../../../hooks/useCourseCardProgress';
import { getCourseCardState } from '../../../utils/courseCardState';

const parseCategories = (value) => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function ClassroomCourses({ classroomId }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const progress = useCourseCardProgress(courses.filter((course) => course.isEnrolled && !course.is_completed).map((course) => course.id));

  const loadCourses = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response = await getTraineeClassroomCourses(classroomId);
      const records = response?.data?.courses || [];
      setCourses(records.map((course) => ({
        id: course.id,
        title: course.course_name,
        description: course.course_desc || 'No description provided.',
        image: course.banner_image,
        modules: Number(course.total_modules || 0),
        enrolled: Number(course.total_users || 0),
        rating: Number(course.total_rating || 0),
        level: course.levels || 'N/A',
        category: parseCategories(course.course_category).join(', ') || 'General',
        price: String(course.course_type || '').toLowerCase() === 'free' ? 'Free' : course.course_price,
        course_type: course.course_type,
        currency: course.currency,
        isEnrolled: Number(course.is_enrolled) === 1,
        is_completed: Number(course.is_completed) === 1,
        classroomId: course.classroom_id,
        classroomName: course.classroom_name,
        access_scope: 'classroom_only',
        has_current_access: true,
        classrooms: [{ id: course.classroom_id, name: course.classroom_name }],
      })));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCourses(); }, [classroomId]);

  const openCourse = (course) => {
    const encoded = encodeData({ id: course.id });
    if (course.isEnrolled) {
      navigate(`/user/courses/WatchCourse/${encodeURIComponent(encoded)}`);
      return;
    }
    navigate(`/user/courses/courseDetails/${encodeURIComponent(encoded)}`, {
      state: { classroomId: course.classroomId, classroomName: course.classroomName },
    });
  };

  if (loading) {
    return <div className="tw-py-16 tw-text-center tw-text-slate-500">Loading classroom courses…</div>;
  }

  if (failed) {
    return <NoData message="Classroom courses are temporarily unavailable" description="Please try again in a moment." onRetry={loadCourses} />;
  }

  if (courses.length === 0) {
    return (
      <NoData
        message="No courses have been added yet"
        description="When your classroom administrator attaches a course, it will appear here."
        icon="fluent:book-open-24-regular"
      />
    );
  }

  return (
    <section className="tw-p-4 md:tw-p-6">
      <div className="tw-mb-5 tw-flex tw-items-center tw-gap-3">
        <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-indigo-50 tw-text-indigo-600"><Icon icon="fluent:book-open-24-regular" className="tw-text-2xl" /></span>
        <div>
          <h5 className="tw-mb-0">Classroom courses</h5>
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Courses available through your membership in this classroom.</p>
        </div>
      </div>
      <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
        {courses.map((course) => {
          const enrollmentState = course.isEnrolled
            ? getCourseCardState(course, 'ready', progress[String(course.id)])
            : {
                isEnrolled: false,
                isCompleted: false,
                progress: null,
                progressLoading: false,
                showPrice: true,
                disablePrimaryAction: false,
              };
          return (
            <CourseCatalogCard
              key={course.id}
              course={course}
              onPrimaryAction={openCourse}
              {...enrollmentState}
              primaryLabel={course.isEnrolled ? 'Continue' : (String(course.course_type).toLowerCase() === 'free' ? 'Enroll' : 'Purchase')}
              compact
            />
          );
        })}
      </div>
    </section>
  );
}
